'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { Text, useTranslation, Image, useUi } from '@weareconceptstudio/core';
import { useUser } from '@weareconceptstudio/account';
import ApplicationButton from '../Footer/ApplicationButton';

//* Utils
import { exclusiveBenefitsCloseSVG } from './utils';

//* Helpers
import { config } from '@/helpers';
import { CLOSE_BOTTOM_POPUPS_EVENT } from '@/helpers/bottomPopups';

//* Styles
import ExclusiveBenefitsStyle from './style';

const STORAGE_KEY_CLOSED = 'exclusive_benefits_popup_closed';
const exclusiveBenefitsAmount = '2,000 AMD';

const getStoredBoolean = (key) => {
	if (typeof sessionStorage === 'undefined') return false;
	try {
		const value = sessionStorage.getItem(key);
		return value ? JSON.parse(value) : false;
	} catch {
		return false;
	}
};

const ExclusiveBenefits = () => {
	const pathname = usePathname();
	const { translate } = useTranslation();
	const { isLoggedIn, user } = useUser();
	const { winWidth } = useUi();

	const [hide, setHide] = useState(false);
	const [onceAnim, setOnceAnim] = useState(false);

	useEffect(() => {
		let timeId;
		timeId = setTimeout(() => {
			setOnceAnim(true);
		}, 1800);
		return () => clearTimeout(timeId);
	}, []);

	const handleClose = () => {
		setHide(true);
		try {
			sessionStorage.setItem(STORAGE_KEY_CLOSED, JSON.stringify(true));
		} catch {}
	};

	useEffect(() => {
		const handler = () => setHide(true);
		window.addEventListener(CLOSE_BOTTOM_POPUPS_EVENT, handler);
		return () => window.removeEventListener(CLOSE_BOTTOM_POPUPS_EVENT, handler);
	}, []);

	const checkShow = useMemo(() => {
		if (typeof window === 'undefined') return false;

		const routes = config.routes || {};
		const pathExcluded =
			pathname.includes(routes.cart?.path) ||
			pathname.includes(routes.checkout?.path) ||
			pathname.includes(routes.account?.path) ||
			pathname.includes(routes.notFound?.path) ||
			pathname.includes(routes.unsubscribe?.path);
		const benefitsClosed = getStoredBoolean(STORAGE_KEY_CLOSED);
		const canShowBenefits = Boolean(user?.showExclusiveBenefits);

		return isLoggedIn && canShowBenefits && !pathExcluded && !benefitsClosed;
	}, [isLoggedIn, user?.showExclusiveBenefits, pathname]);

	if (!checkShow) return null;

	return (
		<ExclusiveBenefitsStyle
			id='exclusive-benefits-wrapper'
			className={`exclusive-benefits-wrapper ${hide ? 'hide' : ''} ${onceAnim ? 'is-first' : ''}`}>
			<div className='exclusive-benefits-inner-wrapper'>
				<div className='exclusive-benefits-inner-wrapper-content'>
					<div className='image-wrapper'>
						<Image src='/images/2000_amd.png' />
					</div>

					<div className='content-wrapper'>
						<div className='content-top-wrapper'>
							<div className='top-row'>
								<div className='title-block'>
									<Text
										vars={{
											amount: <span className='amount-accent'>{exclusiveBenefitsAmount}</span>,
										}}
										text='exclusiveBenefitsTitle'
										className='uppercase p p2 exclusive-benefits-title'
									/>
								</div>
							</div>

							<Text
								text='exclusiveBenefitsDesc'
								className='p p5 exclusive-benefits-description'
							/>
						</div>

						{winWidth >= 768 ? <ApplicationButton isSecondary /> : null}
					</div>
				</div>

				{winWidth < 768 ? <ApplicationButton isSecondary /> : null}

				{/* //! Close button absolute */}
				<div
					className='svg-block'
					onClick={handleClose}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							handleClose();
						}
					}}
					role='button'
					tabIndex={0}
					aria-label={translate('close')}>
					{exclusiveBenefitsCloseSVG}
				</div>
			</div>
		</ExclusiveBenefitsStyle>
	);
};

export default ExclusiveBenefits;
